import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  Tag,
  Space,
  Alert,
  Progress,
  message,
  Spin,
  Empty,
  Modal,
  Input,
  Switch,
  Tabs,
  Badge,
  Dropdown,
  type MenuProps,
} from "antd";
import {
  ArrowLeftOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  MinusOutlined,
  TruckOutlined,
  ReloadOutlined,
  ScanOutlined,
  MoreOutlined,
  InboxOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
  DownloadOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import dayjs from "dayjs";
import { ShippingOptions } from "../components/sendcloud/ShippingOptions";
import { SendcloudShipments } from "../components/sendcloud/SendcloudShipments";
import { sendcloudService } from "../services/sendcloudService";
import { PurchaseOrderManagement } from "../components/containers/PurchaseOrderManagement";

// Extracted imports
import { BookingInfo } from "../types/container-unloading";
import {
  useContainerUnloading,
  type ContainerUnloadingBooking,
} from "../hooks/useContainerUnloading";
import { useProductUpdates } from "../hooks/useProductUpdates";
import { useBarcodeScanner } from "../hooks/useBarcodeScanner.tsx";
import {
  hapticFeedback,
  getStatusColor,
  getStatusText,
  calculateProgress,
} from "../utils/containerUnloadingHelpers";
import { isCustomerEtaOverrideBlockingSendcloudLabels } from "../utils/bookingUtils";
import { generatePalletLabels as generatePalletLabelsUtil } from "../utils/palletLabelGenerator.tsx";
import { ShortcutsModal } from "../components/containers/modals/ShortcutsModal";
import { ContainerResetModal } from "../components/containers/modals/ContainerResetModal";
import { MobileHeader } from "../components/containers/MobileHeader";
import { MobileBottomNav } from "../components/containers/MobileBottomNav";
import { ContainerOverview } from "../components/containers/ContainerOverview";

import "../styles/ContainerUnloading.css";

// Touch optimization - prevent zoom
const preventZoom = (e: TouchEvent) => {
  if (e.touches.length > 1) {
    e.preventDefault();
  }
};

if (typeof document !== "undefined") {
  document.addEventListener("touchstart", preventZoom, { passive: false });
}

const { Title, Text } = Typography;

const ContainerUnloadingPage: React.FC = () => {
  const { containerId } = useParams<{ containerId: string }>();
  const navigate = useNavigate();

  // Use extracted hooks
  const {
    container,
    bookings,
    loading,
    updating,
    startingUnload,
    setBookings,
    setUpdating,
    fetchContainerData,
    startUnloading,
    resetContainer,
  } = useContainerUnloading(containerId);

  const {
    finishingBookings,
    updateProductCartons,
    quickAddCartons,
    quickAddBookingPallets,
    isBookingReadyToFinish,
    finishBooking,
    resetBookingToStep,
  } = useProductUpdates(bookings, setBookings, fetchContainerData);

  // Modal states
  const [palletLabelModalVisible, setPalletLabelModalVisible] = useState(false);
  const [barcodeOptionsModalVisible, setBarcodeOptionsModalVisible] =
    useState(false);
  const [selectedBookingForBarcodes, setSelectedBookingForBarcodes] =
    useState<BookingInfo | null>(null);
  const [palletNote, setPalletNote] = useState("");
  const [deliveryModalVisible, setDeliveryModalVisible] = useState(false);
  const [purchaseOrderModalVisible, setPurchaseOrderModalVisible] =
    useState(false);
  const [containerResetModalVisible, setContainerResetModalVisible] =
    useState(false);
  const [shortcutsModalVisible, setShortcutsModalVisible] = useState(false);
  const [shipmentCreatedModalVisible, setShipmentCreatedModalVisible] =
    useState(false);
  const [selectedBookingForShipment, setSelectedBookingForShipment] =
    useState<BookingInfo | null>(null);
  const [containerOverviewVisible, setContainerOverviewVisible] =
    useState(false);
  const [selectedBooking, setSelectedBooking] =
    useState<ContainerUnloadingBooking | null>(null);

  const [refreshKey, setRefreshKey] = useState(0);

  // UI state - initialize with default, then read from localStorage in useEffect
  // This prevents hydration mismatches on Android/Zebra devices
  const [activeTab, setActiveTabState] = useState<string>("delivery");

  // Set booking status to delivery_planned after shipment creation
  const setBookingToDeliveryPlanned = async (bookingId: number) => {
    try {
      const { error } = await supabase
        .from("bookings")
        .update({
          status: "delivery_planned",
        })
        .eq("id", bookingId);

      if (error) throw error;

      message.success(
        "Booking status set to 'Delivery Planned'! Shipment created successfully."
      );

      // Refresh the data
      fetchContainerData();
    } catch (error) {
      console.error("Error updating booking status:", error);
      message.error("Failed to update booking status");
    }
  };

  // Show modal confirming shipment creation and status update
  const showShipmentCreatedModal = (booking: BookingInfo) => {
    setSelectedBookingForShipment(booking);
    setShipmentCreatedModalVisible(true);
  };

  // Automatically set booking to delivery_planned after shipment creation
  const handleShipmentCreated = async (booking: BookingInfo) => {
    await setBookingToDeliveryPlanned(booking.id);
    showShipmentCreatedModal(booking);
  };

  // Read from localStorage after component mounts (client-side only)
  useEffect(() => {
    if (containerId && typeof window !== "undefined") {
      const savedTab = localStorage.getItem(
        `container-${containerId}-active-tab`
      );
      if (savedTab) {
        setActiveTabState(savedTab);
      }
    }
  }, [containerId]);

  const setActiveTab = (tab: string) => {
    setActiveTabState(tab);
    if (typeof window !== "undefined") {
      localStorage.setItem(`container-${containerId}-active-tab`, tab);
    }
  };

  const [expandedBookings, setExpandedBookings] = useState<Set<number>>(
    new Set()
  );

  const [searchQuery, setSearchQuery] = useState<string>("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Detect mobile device for conditional rendering
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const checkIsMobile = () => {
      const width = window.innerWidth;
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = width < 1025;
      setIsMobile(isSmallScreen || isTouchDevice);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const {
    highlightedProductId,
    productRefs,
    autoScanEnabled,
    setAutoScanEnabled,
    searchProductByEAN,
    scannedProductModal,
    setScannedProductModal,
  } = useBarcodeScanner({
    bookings,
    container,
    loading,
    setExpandedBookings,
    updateProductCartons,
    fetchContainerData,
    setContainerResetModalVisible,
    deliveryModalVisible,
    containerResetModalVisible,
    setShortcutsModalVisible,
    setActiveTab,
    searchQuery,
    expandedBookings,
  });

  // Page-specific helper functions
  const toggleBookingExpanded = (bookingId: number) => {
    if (expandedBookings.has(bookingId)) {
      // If already expanded, collapse it
      const newExpanded = new Set(expandedBookings);
      newExpanded.delete(bookingId);
      setExpandedBookings(newExpanded);
    } else {
      // If not expanded, close all others and open only this one
      setExpandedBookings(new Set([bookingId]));
    }
  };

  const handleDeliveryCreation = (booking: ContainerUnloadingBooking) => {
    if (booking.unloading_status !== "delivery_ready") return;
    setSelectedBooking(booking);
    setDeliveryModalVisible(true);
  };

  const handlePurchaseOrderManagement = (booking: ContainerUnloadingBooking) => {
    setSelectedBooking(booking);
    setPurchaseOrderModalVisible(true);
  };

  const generatePalletLabels = async (
    booking: BookingInfo,
    includeBarcodes: boolean = true
  ) => {
    await generatePalletLabelsUtil(booking, setUpdating, includeBarcodes);
  };

  const downloadShippingLabels = async (bookingId: number) => {
    try {
      // Fetch shipments for this booking
      const { data: shipments, error } = await supabase
        .from("sendcloud_shipments")
        .select("*")
        .eq("booking_id", bookingId)
        .order("created_at", { ascending: false });

      if (error) {
        message.error("Failed to fetch shipments");
        return;
      }

      if (!shipments || shipments.length === 0) {
        message.warning("No shipping labels found for this booking");
        return;
      }

      message.loading(`Downloading ${shipments.length} label(s)...`, 0.5);

      // Download all labels
      for (const shipment of shipments) {
        if (shipment.sendcloud_shipment_id) {
          const result = await sendcloudService.downloadLabel({
            shipmentId: parseInt(shipment.sendcloud_shipment_id),
          });

          if (result.success && result.data) {
            const labelData = result.data as {
              label_url?: string;
              label_data?: string;
              content_type?: string;
            };

            if (labelData.label_data) {
              // Handle base64 PDF data
              try {
                const binaryString = atob(labelData.label_data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }

                const blob = new Blob([bytes], {
                  type: labelData.content_type || "application/pdf",
                });
                const url = window.URL.createObjectURL(blob);
                window.open(url, "_blank");

                // Clean up the URL after a delay
                setTimeout(() => window.URL.revokeObjectURL(url), 1000);
              } catch (error) {
                console.error("Error processing label data:", error);
              }
            } else if (labelData.label_url) {
              window.open(labelData.label_url, "_blank");
            }
          }
        }
      }

      message.success(`${shipments.length} label(s) downloaded`);
    } catch (error) {
      console.error("Error downloading labels:", error);
      message.error("Failed to download labels");
    }
  };

  const downloadAllShippingLabels = async () => {
    try {
      // Get all delivery bookings with completed status
      const completedDeliveryBookings = bookings.filter(
        (b) => b.has_delivery_address && b.unloading_status === "completed"
      );

      if (completedDeliveryBookings.length === 0) {
        message.warning("No completed delivery bookings with shipping labels");
        return;
      }

      // Fetch all shipments for these bookings
      const bookingIds = completedDeliveryBookings.map((b) => b.id);
      const { data: allShipments, error } = await supabase
        .from("sendcloud_shipments")
        .select("*")
        .in("booking_id", bookingIds)
        .order("created_at", { ascending: false });

      if (error) {
        message.error("Failed to fetch shipments");
        return;
      }

      if (!allShipments || allShipments.length === 0) {
        message.warning("No shipping labels found");
        return;
      }

      setUpdating("download-all-labels");
      message.loading(
        `Downloading ${allShipments.length} label(s) from ${completedDeliveryBookings.length} booking(s)...`,
        1
      );

      // Download all labels sequentially with delay
      let downloadedCount = 0;
      for (let i = 0; i < allShipments.length; i++) {
        const shipment = allShipments[i];

        // Add small delay between downloads
        if (i > 0) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }

        if (shipment.sendcloud_shipment_id) {
          console.log(`Downloading label ${i + 1}/${allShipments.length}...`);

          const result = await sendcloudService.downloadLabel({
            shipmentId: parseInt(shipment.sendcloud_shipment_id),
          });

          if (result.success && result.data) {
            const labelData = result.data as {
              label_url?: string;
              label_data?: string;
              content_type?: string;
            };

            if (labelData.label_data) {
              // Handle base64 PDF data - trigger download instead of opening
              try {
                const binaryString = atob(labelData.label_data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }

                const blob = new Blob([bytes], {
                  type: labelData.content_type || "application/pdf",
                });
                const url = window.URL.createObjectURL(blob);

                // Create a temporary link and trigger download
                const link = document.createElement("a");
                link.href = url;
                link.download = `shipping-label-${shipment.booking_id}-${shipment.sendcloud_shipment_id}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Clean up the URL after a delay
                setTimeout(() => window.URL.revokeObjectURL(url), 1000);

                downloadedCount++;
                console.log(
                  `✓ Label ${downloadedCount} downloaded successfully`
                );
              } catch (error) {
                console.error("Error processing label data:", error);
              }
            } else if (labelData.label_url) {
              // For URL-based labels, trigger download
              const link = document.createElement("a");
              link.href = labelData.label_url;
              link.download = `shipping-label-${shipment.booking_id}-${shipment.sendcloud_shipment_id}.pdf`;
              link.target = "_blank";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              downloadedCount++;
              console.log(`✓ Label ${downloadedCount} downloaded successfully`);
            }
          } else {
            console.error("Failed to download label:", result.error);
          }
        }
      }

      setUpdating(null);
      message.success(
        `Downloaded ${downloadedCount} label(s) from ${completedDeliveryBookings.length} booking(s)`
      );
    } catch (error) {
      console.error("Error downloading all labels:", error);
      message.error("Failed to download all labels");
      setUpdating(null);
    }
  };

  // Derived state for UI
  const isUnloadingInProgress = container?.unloading_status === "in_progress";
  const isUnloadingCompleted = container?.unloading_status === "completed";
  const canStartUnloading = !isUnloadingInProgress && !isUnloadingCompleted;

  const { totalBookings, completedBookings, progressPercent } =
    calculateProgress(bookings);

  // Render loading state
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!container) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Empty description="Container not found" />
        <Button
          onClick={() => navigate(`/admin/containers/${containerId}`)}
          style={{ marginTop: 16 }}
        >
          Back to Container Details
        </Button>
      </div>
    );
  }

  return (
    <div
      className="container-unloading-page"
      style={{ padding: "12px 24px", maxWidth: "100vw", margin: "0" }}
    >
      {/* Mobile Header - Only visible on mobile */}
      <MobileHeader
        containerReference={container.reference}
        completedBookings={completedBookings}
        totalBookings={totalBookings}
        progressPercent={progressPercent}
        searchQuery={searchQuery}
        onSearchChange={(value) => {
          setSearchQuery(value);

          // Clear existing timeout
          if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
          }

          // Auto-switch tab if search matches a booking on another tab (immediate)
          if (value.trim().length > 0) {
            const query = value.toLowerCase();

            // Find if any booking matches the search
            const matchedBooking = bookings.find((booking) => {
              const bookingIdMatch = booking.booking_reference.match(/\d+/);
              const bookingId = bookingIdMatch ? bookingIdMatch[0] : "";

              return (
                booking.booking_reference.toLowerCase().includes(query) ||
                bookingId.includes(query) ||
                booking.company_name.toLowerCase().includes(query) ||
                booking.products.some(
                  (product) =>
                    product.name.toLowerCase().includes(query) ||
                    (product.ean && product.ean.toString().includes(query))
                )
              );
            });

            // Switch tab if matched booking is on different tab
            if (matchedBooking) {
              const matchedTab =
                matchedBooking.delivery_type === "delivery"
                  ? "delivery"
                  : "storage";
              if (activeTab !== matchedTab) {
                setActiveTab(matchedTab);
              }

              // Auto-expand the matched booking
              setExpandedBookings(
                (prev) => new Set([...prev, matchedBooking.id])
              );
            }
          }

          // Debounce EAN detection to prevent multiple rapid calls during barcode scanning
          searchTimeoutRef.current = setTimeout(() => {
            // Trigger search for EAN-like values in search input (just highlight, don't increment)
            if (value.length >= 8 && /^\d+$/.test(value)) {
              console.log(
                `🔍 Search input detected EAN-like value: ${value}, highlighting product`
              );
              searchProductByEAN(value, false);
            }
          }, 300); // 300ms debounce
        }}
        autoScanEnabled={autoScanEnabled}
        isUnloadingInProgress={isUnloadingInProgress}
        onBack={() => navigate(`/admin/containers/${containerId}`)}
        onShowHelp={() => setShortcutsModalVisible(true)}
        onShowOverview={() => setContainerOverviewVisible(true)}
      />

      {/* Desktop Header - Only visible on desktop, completely hidden on mobile */}
      {!isMobile && (
        <div className="desktop-only" style={{ marginBottom: 16 }}>
          <Button
            onClick={() => navigate(`/admin/containers/${containerId}`)}
            icon={<ArrowLeftOutlined />}
            style={{ marginBottom: 12 }}
            size="small"
          >
            Back to Container Details
          </Button>

          <Card size="small">
            <Row justify="space-between" align="middle">
              <Col flex="auto">
                <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
                  📦 Container {container.reference} - Unloading Workflow
                </Title>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {completedBookings}/{totalBookings} bookings completed
                  {container.unloading_started_at && (
                    <>
                      {" "}
                      • Started{" "}
                      {dayjs(container.unloading_started_at).format(
                        "DD/MM HH:mm"
                      )}
                    </>
                  )}
                </Text>
              </Col>
              <Col className="desktop-only">
                <Space size="small">
                  <Input
                    placeholder="Search by booking ID, EAN, or product..."
                    value={searchQuery}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchQuery(value);

                      // Clear existing timeout
                      if (searchTimeoutRef.current) {
                        clearTimeout(searchTimeoutRef.current);
                      }

                      // Auto-switch tab if search matches a booking on another tab (immediate)
                      if (value.trim().length > 0) {
                        const query = value.toLowerCase();

                        // Find if any booking matches the search
                        const matchedBooking = bookings.find((booking) => {
                          const bookingIdMatch =
                            booking.booking_reference.match(/\d+/);
                          const bookingId = bookingIdMatch
                            ? bookingIdMatch[0]
                            : "";

                          return (
                            booking.booking_reference
                              .toLowerCase()
                              .includes(query) ||
                            bookingId.includes(query) ||
                            booking.company_name
                              .toLowerCase()
                              .includes(query) ||
                            booking.products.some(
                              (product) =>
                                product.name.toLowerCase().includes(query) ||
                                (product.ean &&
                                  product.ean.toString().includes(query))
                            )
                          );
                        });

                        // Switch tab if matched booking is on different tab
                        if (matchedBooking) {
                          const matchedTab =
                            matchedBooking.delivery_type === "delivery"
                              ? "delivery"
                              : "storage";
                          if (activeTab !== matchedTab) {
                            setActiveTab(matchedTab);
                          }

                          // Auto-expand the matched booking
                          setExpandedBookings(
                            (prev) => new Set([...prev, matchedBooking.id])
                          );
                        }
                      }

                      // Debounce EAN detection to prevent multiple rapid calls during barcode scanning
                      searchTimeoutRef.current = setTimeout(() => {
                        // Trigger search for EAN-like values in search input (just highlight, don't increment)
                        if (value.length >= 8 && /^\d+$/.test(value)) {
                          console.log(
                            `🔍 Desktop search input detected EAN-like value: ${value}, highlighting product`
                          );
                          // Use false for fromScanner - search input should only highlight/open products
                          searchProductByEAN(value, false);
                        }
                      }, 300); // 300ms debounce
                    }}
                    style={{ width: 200 }}
                    size="small"
                    allowClear
                    prefix={<ScanOutlined />}
                  />
                </Space>
              </Col>
              <Col>
                <Space size="small">
                  <Tag
                    color={
                      isUnloadingCompleted
                        ? "green"
                        : isUnloadingInProgress
                        ? "orange"
                        : "default"
                    }
                    style={{ fontSize: "11px" }}
                  >
                    {isUnloadingCompleted
                      ? "Completed"
                      : isUnloadingInProgress
                      ? "In Progress"
                      : "Not Started"}
                  </Tag>
                  {isUnloadingInProgress && (
                    <Space size="small">
                      <ScanOutlined style={{ fontSize: "12px" }} />
                      <Switch
                        checked={autoScanEnabled}
                        onChange={setAutoScanEnabled}
                        size="small"
                      />
                      <Text type="secondary" style={{ fontSize: "11px" }}>
                        Zebra Scanner
                      </Text>
                    </Space>
                  )}
                  {canStartUnloading && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlayCircleOutlined />}
                      onClick={startUnloading}
                      loading={startingUnload}
                      style={{
                        backgroundColor: "#52c41a",
                        borderColor: "#52c41a",
                      }}
                    >
                      Start Unloading
                    </Button>
                  )}
                  {isUnloadingInProgress && (
                    <Button
                      danger
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={() => setContainerResetModalVisible(true)}
                      loading={updating === "container-reset"}
                    >
                      Reset Container
                    </Button>
                  )}
                  <Button
                    size="small"
                    icon={<QuestionCircleOutlined />}
                    onClick={() => setShortcutsModalVisible(true)}
                    title="Keyboard Shortcuts (Ctrl+? or ⌘+?)"
                  >
                    Help
                  </Button>
                </Space>
              </Col>
            </Row>

            {/* Progress Bar */}
            {isUnloadingInProgress && (
              <div style={{ marginTop: 12 }}>
                <Progress
                  percent={Math.round(progressPercent)}
                  status={progressPercent === 100 ? "success" : "active"}
                  size="small"
                  format={() =>
                    `${completedBookings}/${totalBookings} completed`
                  }
                />
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Start Unloading Button - Show when container not started */}
      {canStartUnloading && (
        <div className="start-unload-section" style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            size="large"
            block
            icon={<PlayCircleOutlined />}
            onClick={startUnloading}
            loading={startingUnload}
            style={{
              backgroundColor: "#52c41a",
              borderColor: "#52c41a",
              height: 56,
              fontSize: 18,
              fontWeight: 600,
              borderRadius: 12,
              boxShadow: "0 4px 12px rgba(82, 196, 26, 0.3)",
            }}
          >
            ▶️ Start Unloading Container
          </Button>
        </div>
      )}

      {/* Workflow Steps removed from main view - now in Container Overview modal */}

      {/* Tabbed Bookings Interface */}
      {(() => {
        // Filter bookings based on search query
        const filterBookings = (bookingsToFilter: ContainerUnloadingBooking[]) => {
          if (!searchQuery.trim()) return bookingsToFilter;

          const query = searchQuery.toLowerCase();
          const filtered = bookingsToFilter.filter((booking) => {
            // Extract booking ID from reference (e.g., "EFF508AIR" -> "508")
            const bookingIdMatch = booking.booking_reference.match(/\d+/);
            const bookingId = bookingIdMatch ? bookingIdMatch[0] : "";

            return (
              // Search in full booking reference
              booking.booking_reference.toLowerCase().includes(query) ||
              // Search in booking ID only (e.g., "508" matches "EFF508AIR")
              bookingId.includes(query) ||
              // Search in company name
              booking.company_name.toLowerCase().includes(query) ||
              // Search in products
              booking.products.some(
                (product) =>
                  // Search in product name
                  product.name.toLowerCase().includes(query) ||
                  // Search in EAN
                  (product.ean && product.ean.toString().includes(query))
              )
            );
          });

          // Auto-expand bookings that have matching products when searching
          if (query.length > 2) {
            const bookingsWithMatchingProducts = filtered.filter((booking) =>
              booking.products.some(
                (product) =>
                  product.name.toLowerCase().includes(query) ||
                  (product.ean && product.ean.toString().includes(query))
              )
            );

            if (bookingsWithMatchingProducts.length > 0) {
              const expandIds = bookingsWithMatchingProducts.map((b) => b.id);
              setExpandedBookings((prev) => new Set([...prev, ...expandIds]));
            }
          }

          return filtered;
        };

        // Group bookings by delivery type with search filtering
        const allBookings = filterBookings(bookings).filter(
          (b) => b.id != null && b.id !== undefined
        );
        const deliveryBookings = allBookings.filter(
          (b) => b.delivery_type === "delivery"
        );
        const storageBookings = allBookings.filter(
          (b) => b.delivery_type === "storage"
        );

        const groupedBookings = {
          delivery: deliveryBookings,
          storage: storageBookings,
        };

        const renderCompactBookingCard = (booking: ContainerUnloadingBooking) => {
          // Defensive check: ensure booking has valid ID
          if (!booking || booking.id == null || booking.id === undefined) {
            console.error("Invalid booking data:", booking);
            return null;
          }
          const isExpanded = expandedBookings.has(booking.id);
          const completedProducts = booking.products.filter(
            (p) => p.completed
          ).length;
          const totalProducts = booking.products.length;

          // Calculate cartons scanned vs estimated
          const totalScannedCartons = booking.products.reduce(
            (sum, p) => sum + (p.actual_cartons || 0),
            0
          );
          const totalEstimatedCartons = booking.products.reduce(
            (sum, p) => sum + p.estimated_cartons,
            0
          );

          // Get EAN list for header display (full EANs)
          const eanList = booking.products
            .map((p) => p.ean)
            .filter((ean) => ean != null)
            .map((ean) => String(ean))
            .join(", ");

          // Determine card border and background color based on status
          const getCardStyle = () => {
            if (booking.unloading_status === "completed") {
              return {
                border: "2px solid #52c41a", // Green - completed (PO created or shipped)
                backgroundColor: "#f6ffed", // Light green background
              };
            } else if (
              booking.unloading_status === "counting" ||
              booking.unloading_status === "delivery_ready"
            ) {
              return {
                border: "2px solid #fa8c16", // Orange - in progress
                backgroundColor: "#fff7e6", // Light orange background
              };
            } else {
              return {
                border: "2px solid #d9d9d9", // Grey - not started
                backgroundColor: "#fafafa", // Light grey background
              };
            }
          };

          return (
            <Card
              key={`booking-${booking.id}`}
              className="mobile-booking-card"
              size="small"
              style={{
                marginBottom: 8,
                ...getCardStyle(),
              }}
              data-booking-id={booking.id}
            >
              {/* Compact Header - Always Visible */}
              <div
                className="mobile-booking-header"
                style={{ cursor: "pointer" }}
                onClick={() => toggleBookingExpanded(booking.id)}
              >
                <div className="booking-header-main">
                  {/* Booking Reference - Full width on mobile */}
                  <div className="booking-header-reference">
                    <Text strong style={{ fontSize: 14 }}>
                      {booking.booking_reference}
                    </Text>
                  </div>
                  {/* Badges and Tags - Separate row on mobile */}
                  <div className="booking-header-badges">
                    <Space size={4} wrap>
                      {/* Delivery Step Indicator for delivery bookings */}
                      {booking.has_delivery_address && (
                        <Badge
                          count={
                            booking.unloading_status === "completed"
                              ? "③"
                              : booking.unloading_status === "delivery_ready"
                              ? "②"
                              : "①"
                          }
                          style={{
                            backgroundColor:
                              booking.unloading_status === "completed"
                                ? "#52c41a"
                                : booking.unloading_status === "delivery_ready"
                                ? "#fa8c16"
                                : "#1890ff",
                            fontSize: "11px",
                            fontWeight: "bold",
                          }}
                          title={
                            booking.unloading_status === "completed"
                              ? "Step 3: Ready for Shipment"
                              : booking.unloading_status === "delivery_ready"
                              ? "Step 2: Ready for Delivery Setup"
                              : "Step 1: Counting Products"
                          }
                        />
                      )}
                      <Badge
                        count={`${completedProducts}/${totalProducts}`}
                        style={{
                          backgroundColor:
                            booking.unloading_status === "completed"
                              ? "#52c41a" // Green only when truly completed
                              : completedProducts === totalProducts
                              ? "#fa8c16" // Orange when counted but not completed
                              : "#1890ff", // Blue when still counting
                          fontSize: "10px",
                        }}
                      />
                      <Badge
                        count={`${totalScannedCartons}/${totalEstimatedCartons}`}
                        style={{
                          backgroundColor: "#722ed1",
                          fontSize: "10px",
                        }}
                      />
                      <Tag
                        color={getStatusColor(
                          booking.unloading_status,
                          booking.has_delivery_address
                        )}
                        style={{ fontSize: "10px", margin: 0 }}
                      >
                        {getStatusText(booking)}
                      </Tag>
                      {booking.has_delivery_address ? (
                        <Tag
                          color="blue"
                          style={{ fontSize: "10px", margin: 0 }}
                        >
                          🚚
                        </Tag>
                      ) : (
                        <>
                          <Tag
                            color="orange"
                            style={{ fontSize: "10px", margin: 0 }}
                          >
                            📦
                          </Tag>
                          {/* Only show cartons/pallets if they're greater than 0 */}
                          {((booking.total_actual_cartons &&
                            booking.total_actual_cartons > 0) ||
                            (booking.total_actual_pallets &&
                              booking.total_actual_pallets > 0)) && (
                            <Text
                              type="secondary"
                              style={{ fontSize: "10px", marginLeft: 4 }}
                            >
                              {booking.total_actual_cartons || 0}{" "}
                              {booking.total_actual_pallets || 0}
                            </Text>
                          )}
                        </>
                      )}
                      {booking.shipment_count && booking.shipment_count > 0 && (
                        <Badge
                          count={`${booking.shipment_count} 📄`}
                          style={{
                            backgroundColor: "#52c41a",
                            fontSize: "10px",
                          }}
                          title={`${booking.shipment_count} shipping label(s) created`}
                        />
                      )}
                      {booking.purchase_order_count &&
                        booking.purchase_order_count > 0 && (
                          <Badge
                            count={`${booking.purchase_order_count} 🛒`}
                            style={{
                              backgroundColor: "#fa8c16",
                              fontSize: "10px",
                            }}
                            title={`${booking.purchase_order_count} purchase order(s) created`}
                          />
                        )}
                    </Space>
                  </div>
                  <div className="booking-header-secondary">
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {booking.company_name}
                    </Text>
                    {eanList && (
                      <Text
                        type="secondary"
                        style={{
                          fontSize: "9px",
                          fontFamily: "monospace",
                        }}
                      >
                        • {eanList}
                      </Text>
                    )}
                  </div>
                </div>
                <div className="booking-header-expand">
                  {isExpanded ? "🔽" : "▶️"}
                </div>
              </div>

              {/* Expandable Content - Only show when expanded */}
              {isExpanded && (
                <div
                  className="mobile-booking-content"
                  style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  {/* Action Buttons */}
                  {(booking.unloading_status === "delivery_ready" ||
                    booking.unloading_status === "completed") && (
                    <div style={{ marginBottom: 12 }}>
                      <Space size="small" wrap>
                        {booking.unloading_status === "delivery_ready" &&
                          booking.has_delivery_address && (
                            <Button
                              size="small"
                              type="primary"
                              icon={<TruckOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeliveryCreation(booking);
                              }}
                              style={{
                                backgroundColor: "#722ed1",
                                borderColor: "#722ed1",
                              }}
                            >
                              Ship
                            </Button>
                          )}
                        {booking.unloading_status === "delivery_ready" &&
                          !booking.has_delivery_address && (
                            <>
                              <Button
                                size="small"
                                type="primary"
                                icon={<ShoppingCartOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePurchaseOrderManagement(booking);
                                }}
                                style={{
                                  backgroundColor: "#fa8c16",
                                  borderColor: "#fa8c16",
                                }}
                              >
                                Purchase Order
                              </Button>
                              <Button
                                size="small"
                                type="default"
                                icon={<InboxOutlined />}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const { error } = await supabase
                                    .from("bookings")
                                    .update({ unloading_status: "completed" })
                                    .eq("id", booking.id);

                                  if (error) {
                                    message.error(
                                      "Failed to mark as completed"
                                    );
                                  } else {
                                    message.success(
                                      "Booking marked as stored (no PO)"
                                    );
                                    fetchContainerData();
                                  }
                                }}
                                style={{
                                  backgroundColor: "#d9d9d9",
                                  borderColor: "#d9d9d9",
                                  color: "#595959",
                                }}
                              >
                                Skip PO
                              </Button>
                            </>
                          )}
                        {booking.unloading_status === "completed" && (
                          <>
                            <Button
                              size="small"
                              icon={<EyeOutlined />}
                              disabled
                              onClick={(e) => e.stopPropagation()}
                            >
                              {booking.has_delivery_address
                                ? "Shipped"
                                : "Stored"}
                            </Button>
                            {booking.has_delivery_address && (
                              <Button
                                size="small"
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadShippingLabels(booking.id);
                                }}
                                style={{
                                  backgroundColor: "#1890ff",
                                  borderColor: "#1890ff",
                                }}
                              >
                                Labels
                              </Button>
                            )}
                            {!booking.has_delivery_address && (
                              <>
                                <Button
                                  size="small"
                                  type="default"
                                  icon={<ShoppingCartOutlined />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePurchaseOrderManagement(booking);
                                  }}
                                  style={{
                                    backgroundColor: "#fa8c16",
                                    borderColor: "#fa8c16",
                                    color: "white",
                                  }}
                                >
                                  Purchase Order
                                </Button>
                                {booking.total_actual_pallets &&
                                  booking.total_actual_pallets > 0 && (
                                    <Button
                                      size="small"
                                      type="default"
                                      icon={<InboxOutlined />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedBookingForBarcodes(booking);
                                        setBarcodeOptionsModalVisible(true);
                                      }}
                                      loading={
                                        updating ===
                                        `pallet-labels-${booking.id}`
                                      }
                                      style={{
                                        backgroundColor: "#52c41a",
                                        borderColor: "#52c41a",
                                        color: "white",
                                      }}
                                    >
                                      Labels
                                    </Button>
                                  )}
                              </>
                            )}
                          </>
                        )}
                        {(booking.unloading_status === "delivery_ready" ||
                          booking.unloading_status === "completed") && (
                          <Dropdown
                            menu={{
                              items: [
                                {
                                  key: "reset-counting",
                                  label: "Reset to Counting",
                                  icon: <ReloadOutlined />,
                                  onClick: () =>
                                    resetBookingToStep(booking.id, "counting"),
                                },
                              ] as MenuProps["items"],
                            }}
                            trigger={["click"]}
                            placement="bottomRight"
                          >
                            <Button
                              size="small"
                              icon={<MoreOutlined />}
                              loading={updating === `reset-${booking.id}`}
                              onClick={(e) => e.stopPropagation()}
                              title="Reset options"
                            />
                          </Dropdown>
                        )}
                      </Space>
                    </div>
                  )}

                  {/* Products List - Horizontal Rows */}
                  <div style={{ marginBottom: 12 }}>
                    <Text
                      strong
                      style={{
                        fontSize: 12,
                        marginBottom: 8,
                        display: "block",
                      }}
                    >
                      Products ({booking.products.length})
                    </Text>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {booking.products
                        .filter((p) => p.id != null && p.id !== undefined)
                        .map((product) => (
                        <div
                          key={`product-${product.id}`}
                          className={`mobile-product-row-horizontal ${
                            highlightedProductId === product.id
                              ? "highlighted"
                              : ""
                          } ${product.completed ? "completed" : "pending"}`}
                          ref={(el) => {
                            productRefs.current[product.id] = el;
                          }}
                          style={{
                            backgroundColor:
                              highlightedProductId === product.id
                                ? "#fff7e6"
                                : product.completed
                                ? "#f6ffed"
                                : "#fafafa",
                            border:
                              highlightedProductId === product.id
                                ? "3px solid #fa8c16"
                                : product.completed
                                ? "1px solid #b7eb8f"
                                : "1px solid #d9d9d9",
                            boxShadow:
                              highlightedProductId === product.id
                                ? "0 0 12px rgba(250, 140, 22, 0.5)"
                                : "none",
                            /* Removed transform scale to prevent layout shifts that cause scroll jumping */
                            transition: "all 0.2s ease",
                            zIndex:
                              highlightedProductId === product.id ? 10 : 1,
                            position: "relative" as const,
                            borderRadius: "8px",
                            padding: "10px",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          {/* Left: Product Info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text
                              strong
                              style={{
                                fontSize: "13px",
                                lineHeight: "1.3",
                                display: "block",
                                marginBottom: 3,
                              }}
                            >
                              {product.name}
                            </Text>
                            {product.ean && (
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  fontFamily: "monospace",
                                  color: "#1890ff",
                                  display: "block",
                                  marginBottom: 3,
                                }}
                              >
                                {String(product.ean)}
                              </Text>
                            )}
                            <Text
                              type="secondary"
                              style={{
                                fontSize: "9px",
                                display: "block",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {product.cbm?.toFixed(2) || "-"}m³ •{" "}
                              {product.gross_weight || "-"}kg •{" "}
                              {product.pieces || "-"}pcs • Est:
                              {product.estimated_cartons}
                            </Text>
                          </div>

                          {/* Right: Counter with -/+ buttons */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              flexShrink: 0,
                            }}
                          >
                            {isUnloadingInProgress ? (
                              <>
                                <Button
                                  type="text"
                                  icon={<MinusOutlined />}
                                  onClick={() => {
                                    hapticFeedback();
                                    quickAddCartons(product.id, -1);
                                  }}
                                  disabled={
                                    updating === `product-${product.id}` ||
                                    (product.actual_cartons || 0) === 0
                                  }
                                  style={{
                                    minWidth: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    backgroundColor: "#ff4d4f",
                                    color: "white",
                                    border: "none",
                                    padding: 0,
                                    opacity:
                                      (product.actual_cartons || 0) === 0
                                        ? 0.5
                                        : 1,
                                  }}
                                />
                                <Text
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    color: product.completed
                                      ? "#52c41a"
                                      : "#1890ff",
                                    minWidth: "50px",
                                    textAlign: "center",
                                  }}
                                >
                                  {product.actual_cartons || 0}/
                                  {product.estimated_cartons}
                                </Text>
                                <Button
                                  type="text"
                                  icon={<PlusOutlined />}
                                  onClick={() => {
                                    hapticFeedback();
                                    quickAddCartons(product.id, 1);
                                  }}
                                  disabled={
                                    updating === `product-${product.id}`
                                  }
                                  style={{
                                    minWidth: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    backgroundColor: "#52c41a",
                                    color: "white",
                                    border: "none",
                                    padding: 0,
                                  }}
                                />
                                {!product.completed && (
                                  <Button
                                    type="link"
                                    size="small"
                                    onClick={async () => {
                                      hapticFeedback();
                                      await updateProductCartons(
                                        product.id,
                                        product.estimated_cartons
                                      );
                                    }}
                                    disabled={
                                      updating === `product-${product.id}`
                                    }
                                    style={{
                                      fontSize: "11px",
                                      height: "32px",
                                      padding: "0 8px",
                                      color: "#52c41a",
                                      fontWeight: "600",
                                    }}
                                  >
                                    All
                                  </Button>
                                )}
                              </>
                            ) : (
                              <Text
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  color: product.completed
                                    ? "#52c41a"
                                    : "#1890ff",
                                  minWidth: "50px",
                                  textAlign: "center",
                                }}
                              >
                                {product.actual_cartons || 0}/
                                {product.estimated_cartons}
                              </Text>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Booking-level pallet controls for storage bookings (shown below products) */}
                  {/* Booking-level pallet controls for delivery bookings */}
                  {booking.has_delivery_address && isUnloadingInProgress && (
                    <div style={{ marginBottom: 12, textAlign: "center" }}>
                      <div
                        style={{
                          backgroundColor: "#f0f8ff",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Text
                          style={{
                            fontSize: "12px",
                            fontWeight: "bold",
                            color: "#1890ff",
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          🚚 Delivery Pallets
                        </Text>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                          }}
                        >
                          <Button
                            className="mobile-counter-btn"
                            type="primary"
                            danger
                            icon={<MinusOutlined />}
                            onClick={() => {
                              hapticFeedback();
                              quickAddBookingPallets(booking.id, -1);
                            }}
                            disabled={
                              updating === `booking-pallets-${booking.id}`
                            }
                            style={{
                              backgroundColor: "#1890ff",
                              borderColor: "#1890ff",
                            }}
                          />
                          <div
                            className="mobile-counter-display"
                            style={{
                              backgroundColor: "#f0f8ff",
                              border: "2px solid #1890ff",
                              fontSize: "18px",
                            }}
                          >
                            {booking.total_actual_pallets || 0}
                          </div>
                          <Button
                            className="mobile-counter-btn"
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                              hapticFeedback();
                              quickAddBookingPallets(booking.id, 1);
                            }}
                            disabled={
                              updating === `booking-pallets-${booking.id}`
                            }
                            style={{
                              backgroundColor: "#1890ff",
                              borderColor: "#1890ff",
                            }}
                          />
                        </div>
                        <Text
                          type="secondary"
                          style={{ fontSize: "10px", marginTop: "4px" }}
                        >
                          Mixed products per pallet
                        </Text>
                      </div>
                    </div>
                  )}

                  {/* Finish Button - appears when all cartons are counted */}
                  {isBookingReadyToFinish(booking) &&
                    booking.unloading_status !== "delivery_ready" &&
                    booking.unloading_status !== "completed" && (
                      <div style={{ marginBottom: 12, textAlign: "center" }}>
                        <Button
                          type="primary"
                          size="large"
                          block
                          icon={<CheckCircleOutlined />}
                          onClick={() => finishBooking(booking)}
                          loading={finishingBookings.has(booking.id)}
                          style={{
                            backgroundColor: "#52c41a",
                            borderColor: "#52c41a",
                            height: "48px",
                            fontSize: "16px",
                            fontWeight: "600",
                          }}
                        >
                          ✓ Finish Booking
                        </Button>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#52c41a",
                            marginTop: "4px",
                            fontWeight: "500",
                          }}
                        >
                          Complete booking and calculate totals
                        </div>
                      </div>
                    )}

                  {/* Generate Pallet Labels Button */}
                  {booking.total_actual_pallets &&
                    booking.total_actual_pallets > 0 &&
                    (booking.unloading_status === "delivery_ready" ||
                      booking.unloading_status === "completed") && (
                      <div style={{ marginBottom: 12, textAlign: "center" }}>
                        <Button
                          type="default"
                          icon={<InboxOutlined />}
                          onClick={() => {
                            setSelectedBookingForBarcodes(booking);
                            setPalletNote(booking.internal_pallet_label_note || "");
                            setBarcodeOptionsModalVisible(true);
                          }}
                          loading={updating === `pallet-labels-${booking.id}`}
                          size="small"
                          style={{
                            backgroundColor: "#52c41a",
                            borderColor: "#52c41a",
                            color: "white",
                          }}
                        >
                          🏷️ Generate Pallet Labels
                        </Button>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#8c8c8c",
                            marginTop: "4px",
                          }}
                        >
                          Print labels for {booking.total_actual_pallets}{" "}
                          pallet(s)
                        </div>
                      </div>
                    )}


                  {/* Summary info - show totals when booking is finished */}
                  {(booking.total_actual_cartons !== null &&
                    booking.total_actual_cartons !== undefined &&
                    booking.total_actual_cartons > 0) ||
                  (booking.total_actual_pallets !== null &&
                    booking.total_actual_pallets !== undefined &&
                    booking.total_actual_pallets > 0) ||
                  booking.delivery_info?.shipment_status ? (
                    <div
                      style={{
                        padding: "6px 8px",
                        backgroundColor: "#f0f0f0",
                        borderRadius: 4,
                        fontSize: "11px",
                      }}
                    >
                      <Space size="small">
                        {booking.total_actual_cartons &&
                          booking.total_actual_cartons > 0 && (
                            <Text type="secondary">
                              Total: {booking.total_actual_cartons} cartons
                            </Text>
                          )}
                        {booking.total_actual_pallets &&
                          booking.total_actual_pallets > 0 && (
                            <Text type="secondary">
                              Pallets: {booking.total_actual_pallets}
                            </Text>
                          )}
                        {booking.delivery_info?.shipment_status && (
                          <Text type="secondary">
                            Status:{" "}
                            {booking.delivery_info.shipment_status.toUpperCase()}
                          </Text>
                        )}
                      </Space>
                    </div>
                  ) : null}
                </div>
              )}
            </Card>
          );
        };

        return (
          <Tabs
            className="mobile-tabs"
            activeKey={activeTab}
            onChange={setActiveTab}
            size="large"
            items={[
              {
                key: "delivery",
                label: (
                  <Badge
                    count={groupedBookings.delivery.length}
                    size="small"
                    offset={[10, 0]}
                  >
                    <span
                      style={{
                        fontSize: "16px",
                        padding: "0 16px",
                        fontWeight: 500,
                      }}
                    >
                      🚚 Delivery
                    </span>
                  </Badge>
                ),
                children: (
                  <div>
                    <div className="mobile-section-header">
                      <span className="section-icon">🚚</span>
                      <span className="section-title">Delivery Bookings</span>
                      <span className="section-count">
                        {groupedBookings.delivery.length}
                      </span>
                    </div>

                    {/* Download All Labels Button */}
                    {groupedBookings.delivery.some(
                      (b) =>
                        b.unloading_status === "completed" &&
                        b.shipment_count &&
                        b.shipment_count > 0
                    ) && (
                      <div style={{ marginBottom: 16, padding: "0 8px" }}>
                        <Button
                          type="primary"
                          icon={<DownloadOutlined />}
                          onClick={downloadAllShippingLabels}
                          loading={updating === "download-all-labels"}
                          block
                          size="large"
                          style={{
                            backgroundColor: "#52c41a",
                            borderColor: "#52c41a",
                            fontWeight: 600,
                          }}
                        >
                          📄 Download All Shipping Labels (
                          {groupedBookings.delivery
                            .filter(
                              (b) => b.shipment_count && b.shipment_count > 0
                            )
                            .reduce(
                              (sum, b) => sum + (b.shipment_count || 0),
                              0
                            )}
                          )
                        </Button>
                      </div>
                    )}

                    {groupedBookings.delivery
                      .map(renderCompactBookingCard)
                      .filter((card) => card !== null)}
                    {groupedBookings.delivery.length === 0 && (
                      <Empty
                        description={
                          searchQuery
                            ? "No delivery bookings match your search"
                            : "No delivery bookings found"
                        }
                      />
                    )}
                  </div>
                ),
              },
              {
                key: "storage",
                label: (
                  <Badge
                    count={groupedBookings.storage.length}
                    size="small"
                    offset={[10, 0]}
                  >
                    <span
                      style={{
                        fontSize: "16px",
                        padding: "0 16px",
                        fontWeight: 500,
                      }}
                    >
                      📦 Storage
                    </span>
                  </Badge>
                ),
                children: (
                  <div>
                    <div className="mobile-section-header storage">
                      <span className="section-icon">📦</span>
                      <span className="section-title">Storage Bookings</span>
                      <span className="section-count">
                        {groupedBookings.storage.length}
                      </span>
                    </div>
                    {groupedBookings.storage
                      .map(renderCompactBookingCard)
                      .filter((card) => card !== null)}
                    {groupedBookings.storage.length === 0 && (
                      <Empty description="No storage bookings found" />
                    )}
                  </div>
                ),
              },
            ]}
          />
        );
      })()}

      {/* Pallet Modal removed - palletizing step is skipped */}

      {/* Create Shipment Modal */}
      <Modal
        className="mobile-modal shipment-modal"
        title={
          <div>
            🚚 {selectedBooking?.booking_reference}
            <div
              style={{
                fontSize: "12px",
                fontWeight: "normal",
                opacity: 0.8,
                marginTop: 2,
              }}
            >
              {selectedBooking?.total_actual_cartons || 0} cartons •{" "}
              {selectedBooking?.total_actual_pallets || 0} pallets
            </div>
          </div>
        }
        open={deliveryModalVisible}
        onCancel={() => {
          setDeliveryModalVisible(false);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setDeliveryModalVisible(false);
            }}
          >
            Close
          </Button>,
        ]}
        width="100%"
      >
        {selectedBooking?.delivery_info ? (
          <div>
            <ShippingOptions
              deliveryInfo={{
                country: selectedBooking.delivery_info.country,
                postal_code: selectedBooking.delivery_info.postal_code,
                city: selectedBooking.delivery_info.city,
                street_name: selectedBooking.delivery_info.street_name,
                house_number: selectedBooking.delivery_info.house_number,
              }}
              customerEta={selectedBooking.eta ?? null}
              bookingId={selectedBooking.id}
              deliveryCartons={
                selectedBooking.total_actual_cartons || undefined
              }
              deliveryPallets={
                selectedBooking.total_actual_pallets || undefined
              }
              onCreateShipment={(shippingMethodId) => {
                console.log("Create shipment with method:", shippingMethodId);
                setRefreshKey((prev) => prev + 1);
              }}
              onShipmentCreated={() => {
                setRefreshKey((prev) => prev + 1);
                // Automatically set booking to delivery_planned and show confirmation
                handleShipmentCreated(selectedBooking);
                // Close the modal after successful shipment creation
                setDeliveryModalVisible(false);
              }}
              disabled={
                ![
                  "customs_cleared",
                  "delivery_planned",
                  "out_for_delivery",
                  "delivered",
                  "added_to_stock",
                ].includes(selectedBooking.status) ||
                isCustomerEtaOverrideBlockingSendcloudLabels(
                  selectedBooking.eta ?? null
                )
              }
            />

            <div style={{ marginTop: 24 }}>
              <SendcloudShipments
                key={refreshKey}
                bookingId={selectedBooking.id}
                onRefresh={() => {
                  setRefreshKey((prev) => prev + 1);
                }}
              />
            </div>
          </div>
        ) : (
          <Alert
            message="No Delivery Address"
            description="This booking doesn't have a delivery address set up. Please add delivery information first."
            type="warning"
          />
        )}
      </Modal>

      {/* Purchase Order Modal */}
      <Modal
        className="mobile-modal purchase-order-modal"
        title={
          <div>
            📦 {selectedBooking?.booking_reference}
            <div
              style={{
                fontSize: "12px",
                fontWeight: "normal",
                opacity: 0.8,
                marginTop: 2,
              }}
            >
              {selectedBooking?.total_actual_cartons || 0} cartons •{" "}
              {selectedBooking?.total_actual_pallets || 0} pallets
            </div>
          </div>
        }
        open={purchaseOrderModalVisible}
        onCancel={() => {
          setPurchaseOrderModalVisible(false);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setPurchaseOrderModalVisible(false);
            }}
          >
            Close
          </Button>,
        ]}
        width="100%"
      >
        {selectedBooking && (
          <PurchaseOrderManagement
            bookingId={selectedBooking.id}
            bookingReference={selectedBooking.booking_reference}
            companyId={selectedBooking.company_id}
            onRefresh={() => {
              setRefreshKey((prev) => prev + 1);
              fetchContainerData();
            }}
          />
        )}
      </Modal>

      {/* Container Overview Modal */}
      <ContainerOverview
        visible={containerOverviewVisible}
        onClose={() => setContainerOverviewVisible(false)}
        containerReference={container.reference}
        bookings={bookings}
        isUnloadingInProgress={isUnloadingInProgress}
        isUnloadingCompleted={isUnloadingCompleted}
        canStartUnloading={canStartUnloading}
        startingUnload={startingUnload}
        completedBookings={completedBookings}
        totalBookings={totalBookings}
        progressPercent={progressPercent}
        onStartUnloading={startUnloading}
        onResetContainer={() => {
          setContainerOverviewVisible(false);
          setContainerResetModalVisible(true);
        }}
        autoScanEnabled={autoScanEnabled}
        onToggleAutoScan={() => setAutoScanEnabled(!autoScanEnabled)}
      />

      {/* Keyboard Shortcuts Modal */}
      <ShortcutsModal
        visible={shortcutsModalVisible}
        onClose={() => setShortcutsModalVisible(false)}
      />

      {/* Pallet Labels Download Modal */}
      <Modal
        className="mobile-modal"
        title={
          <Space>
            <InboxOutlined style={{ color: "#52c41a" }} />
            🏷️ Download Pallet Labels - {selectedBooking?.booking_reference}
          </Space>
        }
        open={palletLabelModalVisible}
        onCancel={() => setPalletLabelModalVisible(false)}
        footer={[
          <Button key="skip" onClick={() => setPalletLabelModalVisible(false)}>
            Skip for Now
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<InboxOutlined />}
            onClick={async () => {
              if (selectedBooking) {
                await generatePalletLabels(selectedBooking, true);
                setPalletLabelModalVisible(false);
              }
            }}
            loading={updating?.startsWith("pallet-labels-")}
            style={{
              backgroundColor: "#52c41a",
              borderColor: "#52c41a",
            }}
          >
            🏷️ Download Labels
          </Button>,
        ]}
        width="100%"
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: "16px", marginBottom: "16px" }}>
            ✅ Palletizing completed successfully!
          </div>

          {selectedBooking && (
            <Alert
              message={`Ready to generate ${selectedBooking.total_actual_pallets} pallet label(s)`}
              description={
                <div style={{ marginTop: "8px" }}>
                  <div>
                    📦 Total Cartons: {selectedBooking.total_actual_cartons}
                  </div>
                  <div>
                    🏷️ Total Pallets: {selectedBooking.total_actual_pallets}
                  </div>
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "12px",
                      color: "#8c8c8c",
                    }}
                  >
                    Labels will be downloaded as individual PDF files
                  </div>
                </div>
              }
              type="success"
              showIcon
              style={{ marginBottom: "16px" }}
            />
          )}

          <div style={{ color: "#8c8c8c", fontSize: "14px" }}>
            You can also download these labels later from the booking card.
          </div>
        </div>
      </Modal>

      {/* Barcode Options Modal */}
      <Modal
        className="mobile-modal"
        title={
          <Space>
            <InboxOutlined style={{ color: "#52c41a" }} />
            🏷️ Generate Pallet Labels
          </Space>
        }
        open={barcodeOptionsModalVisible}
        onCancel={() => {
          setBarcodeOptionsModalVisible(false);
          setSelectedBookingForBarcodes(null);
          setPalletNote("");
        }}
        footer={null}
        width="100%"
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: "16px", marginBottom: "16px" }}>
            Generate pallet labels for{" "}
            <strong>{selectedBookingForBarcodes?.booking_reference}</strong>
          </div>

          {selectedBookingForBarcodes && (
            <Alert
              message={`${selectedBookingForBarcodes.total_actual_pallets} pallet label(s) will be generated`}
              description={
                <div style={{ marginTop: "8px" }}>
                  <div>
                    📦 Total Cartons:{" "}
                    {selectedBookingForBarcodes.total_actual_cartons}
                  </div>
                  <div>
                    🏷️ Pallets:{" "}
                    {selectedBookingForBarcodes.total_actual_pallets}
                  </div>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: "20px" }}
            />
          )}

          {/* Pallet Note Input */}
          <div style={{ marginBottom: "20px", textAlign: "left" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "8px",
                color: "#262626",
              }}
            >
              📝 Pallet Note
            </label>
            <Input.TextArea
              value={palletNote}
              onChange={(e) => setPalletNote(e.target.value)}
              placeholder="Add a note that will appear on the pallet label..."
              rows={3}
              style={{
                borderRadius: "6px",
                resize: "vertical",
              }}
            />
            <div
              style={{
                fontSize: "12px",
                color: "#8c8c8c",
                marginTop: "4px",
              }}
            >
              This note will be printed on all pallet labels for this booking
            </div>
          </div>

          {/* Barcode Options Cards */}
          <div
            style={{
              display: "flex",
              gap: "16px",
              justifyContent: "center",
              marginBottom: "16px",
            }}
          >
            <div
              onClick={async () => {
                if (selectedBookingForBarcodes) {
                  // Save pallet note first
                  const { error } = await supabase
                    .from("bookings")
                    .update({ internal_pallet_label_note: palletNote })
                    .eq("id", selectedBookingForBarcodes.id);

                  if (error) {
                    message.error("Failed to save pallet note");
                    return;
                  }

                  // Update local state
                  setBookings((prevBookings) =>
                    prevBookings.map((b) =>
                      b.id === selectedBookingForBarcodes.id
                        ? { ...b, internal_pallet_label_note: palletNote }
                        : b
                    )
                  );

                  // Generate labels with updated booking
                  const updatedBooking = { ...selectedBookingForBarcodes, internal_pallet_label_note: palletNote };
                  await generatePalletLabels(updatedBooking, false);
                  setBarcodeOptionsModalVisible(false);
                  setSelectedBookingForBarcodes(null);
                  setPalletNote("");
                }
              }}
              style={{
                flex: 1,
                padding: "20px",
                border: "2px solid #d9d9d9",
                borderRadius: "8px",
                cursor: "pointer",
                backgroundColor: "#fafafa",
                transition: "all 0.2s",
                maxWidth: "200px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#1890ff";
                e.currentTarget.style.backgroundColor = "#f0f8ff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#d9d9d9";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
            >
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "8px",
                }}
              >
                🏷️ Labels Only
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {selectedBookingForBarcodes?.has_delivery_address
                  ? "1 page per pallet"
                  : "1 page total"}
              </div>
            </div>

            <div
              onClick={async () => {
                if (selectedBookingForBarcodes) {
                  // Save pallet note first
                  const { error } = await supabase
                    .from("bookings")
                    .update({ internal_pallet_label_note: palletNote })
                    .eq("id", selectedBookingForBarcodes.id);

                  if (error) {
                    message.error("Failed to save pallet note");
                    return;
                  }

                  // Update local state
                  setBookings((prevBookings) =>
                    prevBookings.map((b) =>
                      b.id === selectedBookingForBarcodes.id
                        ? { ...b, internal_pallet_label_note: palletNote }
                        : b
                    )
                  );

                  // Generate labels with updated booking
                  const updatedBooking = { ...selectedBookingForBarcodes, internal_pallet_label_note: palletNote };
                  await generatePalletLabels(updatedBooking, true);
                  setBarcodeOptionsModalVisible(false);
                  setSelectedBookingForBarcodes(null);
                  setPalletNote("");
                }
              }}
              style={{
                flex: 1,
                padding: "20px",
                border: "2px solid #d9d9d9",
                borderRadius: "8px",
                cursor: "pointer",
                backgroundColor: "#fafafa",
                transition: "all 0.2s",
                maxWidth: "200px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#52c41a";
                e.currentTarget.style.backgroundColor = "#f6ffed";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#d9d9d9";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
            >
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  marginBottom: "8px",
                }}
              >
                🏷️ + 📱 Barcodes
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {selectedBookingForBarcodes?.has_delivery_address
                  ? "2 pages per pallet"
                  : "2 pages total"}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Container Reset Confirmation Modal */}
      <ContainerResetModal
        visible={containerResetModalVisible}
        loading={updating === "container-reset"}
        onClose={() => setContainerResetModalVisible(false)}
        onConfirm={resetContainer}
      />

      {/* Scanned Product Modal */}
      <Modal
        className="mobile-modal scanned-product-modal"
        title={
          <div style={{ textAlign: "center" }}>
            <div
              style={{ fontSize: "18px", fontWeight: "bold", color: "#1890ff" }}
            >
              {scannedProductModal.booking?.has_delivery_address ? "🚚" : "📦"}{" "}
              {scannedProductModal.product?.name}
            </div>
            <div style={{ fontSize: "14px", color: "#666", marginTop: "4px" }}>
              {scannedProductModal.booking?.booking_reference}
            </div>
            {/* Clear delivery/storage indicator */}
            <div style={{ marginTop: "8px" }}>
              <Tag
                color={
                  scannedProductModal.booking?.has_delivery_address
                    ? "blue"
                    : "orange"
                }
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "4px 12px",
                }}
              >
                {scannedProductModal.booking?.has_delivery_address ? (
                  <>🚚 DELIVERY BOOKING</>
                ) : (
                  <>📦 STORAGE BOOKING</>
                )}
              </Tag>
            </div>
          </div>
        }
        open={scannedProductModal.visible}
        onCancel={() =>
          setScannedProductModal({
            product: null,
            booking: null,
            visible: false,
          })
        }
        footer={[
          <Button
            key="close"
            onClick={() =>
              setScannedProductModal({
                product: null,
                booking: null,
                visible: false,
              })
            }
            style={{ width: "100%" }}
          >
            Continue Scanning
          </Button>,
        ]}
        width="90%"
        style={{ maxWidth: "360px" }}
        centered
      >
        {scannedProductModal.product && scannedProductModal.booking && (
          <div style={{ padding: "8px 0" }}>
            {/* Product Details */}
            <div style={{ marginBottom: "8px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "6px",
                }}
              >
                <Text strong style={{ fontSize: "13px" }}>
                  Product Details
                </Text>
                <Tag
                  color="blue"
                  style={{
                    fontSize: "10px",
                    padding: "1px 4px",
                    height: "18px",
                  }}
                >
                  {scannedProductModal.product.ean}
                </Tag>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "4px",
                  fontSize: "11px",
                  lineHeight: "1.2",
                }}
              >
                <div>
                  <Text type="secondary" style={{ fontSize: "10px" }}>
                    Dimensions:
                  </Text>
                  <div style={{ fontWeight: "500" }}>
                    {scannedProductModal.product.cbm?.toFixed(2) || "-"} m³
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: "10px" }}>
                    Weight:
                  </Text>
                  <div style={{ fontWeight: "500" }}>
                    {scannedProductModal.product.gross_weight || "-"} kg
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: "10px" }}>
                    Pieces:
                  </Text>
                  <div style={{ fontWeight: "500" }}>
                    {scannedProductModal.product.pieces || "-"} pcs
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: "10px" }}>
                    Expected:
                  </Text>
                  <div style={{ fontWeight: "500" }}>
                    {scannedProductModal.product.estimated_cartons} cartons
                  </div>
                </div>
              </div>
            </div>

            {/* Current Count */}
            <div style={{ marginBottom: "8px" }}>
              <div style={{ marginBottom: "4px" }}>
                <Text strong style={{ fontSize: "12px", color: "#52c41a" }}>
                  Current Count:{" "}
                  {scannedProductModal.product.actual_cartons || 0} /{" "}
                  {scannedProductModal.product.estimated_cartons} cartons
                </Text>
              </div>
              <Progress
                percent={Math.round(
                  ((scannedProductModal.product.actual_cartons || 0) /
                    scannedProductModal.product.estimated_cartons) *
                    100
                )}
                size="small"
                strokeColor="#52c41a"
                showInfo={false}
                style={{ margin: 0 }}
              />
            </div>

            {/* Quick Actions */}
            {isUnloadingInProgress && (
              <div style={{ marginTop: "8px" }}>
                <Text
                  strong
                  style={{
                    fontSize: "12px",
                    marginBottom: "6px",
                    display: "block",
                  }}
                >
                  Quick Actions
                </Text>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "4px",
                  }}
                >
                  <Button
                    size="small"
                    danger
                    onClick={() => {
                      hapticFeedback();
                      quickAddCartons(scannedProductModal.product!.id, -1);
                      // Update modal product count for real-time display
                      setScannedProductModal((prev) => ({
                        ...prev,
                        product: prev.product
                          ? {
                              ...prev.product,
                              actual_cartons: Math.max(
                                0,
                                (prev.product.actual_cartons || 0) - 1
                              ),
                            }
                          : null,
                      }));
                    }}
                    disabled={
                      updating ===
                        `product-${scannedProductModal.product.id}` ||
                      (scannedProductModal.product!.actual_cartons || 0) === 0
                    }
                    style={{
                      height: "32px",
                      padding: "0 8px",
                      fontSize: "12px",
                      backgroundColor: "#ff4d4f !important",
                      borderColor: "#ff4d4f !important",
                      color: "white !important",
                    }}
                  >
                    -
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      hapticFeedback();
                      quickAddCartons(scannedProductModal.product!.id, 1);
                      // Update modal product count for real-time display
                      setScannedProductModal((prev) => ({
                        ...prev,
                        product: prev.product
                          ? {
                              ...prev.product,
                              actual_cartons:
                                (prev.product.actual_cartons || 0) + 1,
                            }
                          : null,
                      }));
                    }}
                    disabled={
                      updating === `product-${scannedProductModal.product.id}`
                    }
                    style={{
                      backgroundColor: "#52c41a",
                      borderColor: "#52c41a",
                      height: "32px",
                      padding: "0 8px",
                      fontSize: "12px",
                    }}
                  >
                    +
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      hapticFeedback();
                      const remainingCartons =
                        scannedProductModal.product!.estimated_cartons -
                        (scannedProductModal.product!.actual_cartons || 0);
                      if (remainingCartons > 0) {
                        updateProductCartons(
                          scannedProductModal.product!.id,
                          scannedProductModal.product!.estimated_cartons
                        );
                        // Update modal product count to show completion
                        setScannedProductModal((prev) => ({
                          ...prev,
                          product: prev.product
                            ? {
                                ...prev.product,
                                actual_cartons: prev.product.estimated_cartons,
                              }
                            : null,
                        }));
                      }
                    }}
                    disabled={
                      updating ===
                        `product-${scannedProductModal.product.id}` ||
                      (scannedProductModal.product!.actual_cartons || 0) >=
                        scannedProductModal.product!.estimated_cartons
                    }
                    style={{
                      height: "32px",
                      padding: "0 8px",
                      fontSize: "11px",
                    }}
                  >
                    +
                    {Math.max(
                      0,
                      scannedProductModal.product!.estimated_cartons -
                        (scannedProductModal.product!.actual_cartons || 0)
                    )}
                  </Button>
                </div>
                <div
                  style={{
                    marginTop: "6px",
                    paddingTop: "6px",
                    borderTop: "1px solid #f0f0f0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Button
                    size="small"
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => {
                      if (!scannedProductModal.booking) return;

                      // Save booking data before closing modal
                      const bookingToFocus = scannedProductModal.booking;

                      // Close the scanned product modal
                      setScannedProductModal({
                        product: null,
                        booking: null,
                        visible: false,
                      });

                      // Switch to the correct tab and expand the booking
                      setActiveTab(
                        bookingToFocus.has_delivery_address
                          ? "delivery"
                          : "storage"
                      );
                      setExpandedBookings(
                        (prev) => new Set([...prev, bookingToFocus.id])
                      );

                      // Brief delay to ensure tab switch and expansion complete, then scroll
                      setTimeout(() => {
                        const bookingElement = document.querySelector(
                          `[data-booking-id="${bookingToFocus.id}"]`
                        );
                        if (bookingElement) {
                          bookingElement.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        } else {
                          console.warn(
                            `Booking element not found for ID: ${bookingToFocus.id}`
                          );
                        }
                      }, 300);
                    }}
                    style={{ fontSize: "12px", padding: "4px 8px" }}
                  >
                    View Full Booking
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Mobile Bottom Navigation - Only visible on mobile */}
      <MobileBottomNav
        activeTab={activeTab}
        onTabChange={setActiveTab}
        deliveryCount={
          bookings.filter((b) => b.delivery_type === "delivery").length
        }
        storageCount={
          bookings.filter((b) => b.delivery_type === "storage").length
        }
        completedCount={completedBookings}
        totalCount={totalBookings}
        autoScanEnabled={autoScanEnabled}
        onToggleAutoScan={() => setAutoScanEnabled(!autoScanEnabled)}
        onShowOverview={() => setContainerOverviewVisible(true)}
        isUnloadingInProgress={isUnloadingInProgress}
        onResetContainer={() => setContainerResetModalVisible(true)}
      />

      {/* Shipment Created Confirmation Modal */}
      <Modal
        title="Shipment Created Successfully! 🎉"
        open={shipmentCreatedModalVisible}
        onCancel={() => setShipmentCreatedModalVisible(false)}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => setShipmentCreatedModalVisible(false)}
          >
            OK
          </Button>,
        ]}
        centered
      >
        <div style={{ padding: "20px 0" }}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🚚</div>
            <h3 style={{ marginBottom: 8 }}>Delivery Planned!</h3>
          </div>

          <p style={{ marginBottom: 16 }}>
            Your Sendcloud shipment has been created successfully for booking{" "}
            <strong>{selectedBookingForShipment?.booking_reference}</strong> and
            the booking status has been automatically set to{" "}
            <strong>"Delivery Planned"</strong>.
          </p>

          <div
            style={{
              background: "#f6ffed",
              border: "1px solid #b7eb8f",
              padding: 12,
              borderRadius: 6,
            }}
          >
            <p style={{ margin: 0, color: "#52c41a", fontWeight: 500 }}>
              📋 This indicates that delivery has been planned and scheduled for
              this booking.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ContainerUnloadingPage;
